import React, { useState, useEffect, useMemo } from 'react';
import {
    TextField, Autocomplete, Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Checkbox, FormControlLabel, Box, Tooltip, Typography, Stack, Paper, IconButton, Divider,
    useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { createFilterOptions } from '@mui/material/Autocomplete';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import SaveIcon from '@mui/icons-material/Save';
import BoltIcon from '@mui/icons-material/Bolt';
import BuildIcon from '@mui/icons-material/Build';
import MemoryIcon from '@mui/icons-material/Memory';
import { API } from '../App';

const filter = createFilterOptions({
    stringify: (option) => {
        if (!option) return '';
        if (option.inputValue) return option.inputValue;
        return `${option.akz || ''} ${option.name || ''}`;
    }
});

const stripSuffix = (akz) => (typeof akz === 'string' ? akz.split('#')[0] : (akz || ''));

function PropIcon({ active, icon, label }) {
    if (!active) return null;
    return (
        <Tooltip title={label}>
            <Box
                component="span"
                sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.4,
                    px: 0.75,
                    py: 0.2,
                    borderRadius: 1,
                    bgcolor: 'action.hover',
                    color: 'primary.main',
                    fontSize: 11
                }}
            >
                {React.cloneElement(icon, { sx: { fontSize: 14 } })}
                {label}
            </Box>
        </Tooltip>
    );
}

export default function SecureEntryList({ secureEntries, onUpdate, jobNr }) {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [entries, setEntries] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [resolvedMap, setResolvedMap] = useState({});
    const [inputValue, setInputValue] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [draft, setDraft] = useState({
        akz: '', name: '', description: '', comment: '',
        electric: false, mechanic: false, pls: false
    });
    const [editOpen, setEditOpen] = useState(false);
    const [editAkz, setEditAkz] = useState(null);
    const [editDraft, setEditDraft] = useState({
        name: '', description: '', comment: '',
        electric: false, mechanic: false, pls: false
    });
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        setEntries(Array.isArray(secureEntries) ? secureEntries : []);
    }, [secureEntries]);

    useEffect(() => {
        if (!jobNr) return;
        let cancelled = false;
        (async () => {
            const res = await API.getSecureEntries();
            if (!cancelled && res?.success) setTemplates(Array.isArray(res.msg) ? res.msg : []);
        })();
        return () => { cancelled = true; };
    }, [jobNr]);

    useEffect(() => {
        if (!jobNr) return;
        const list = Array.isArray(secureEntries) ? secureEntries : [];
        if (list.length === 0) return;
        let cancelled = false;
        (async () => {
            const res = await API.resolveSecureEntries(list);
            if (!cancelled && res?.success && Array.isArray(res.msg)) {
                const map = {};
                for (const e of res.msg) if (e?.akz) map[e.akz] = e;
                setResolvedMap((prev) => ({ ...prev, ...map }));
            }
        })();
        return () => { cancelled = true; };
    }, [secureEntries, jobNr]);

    const cacheEntry = (entry) => {
        if (!entry?.akz) return;
        setResolvedMap((prev) => ({ ...prev, [entry.akz]: entry }));
    };

    const commit = (next) => { setEntries(next); if (onUpdate) onUpdate(next); };
    const appendAkz = (akz) => { if (akz) commit([...entries, akz]); };
    const handleRemove = (idx) => commit(entries.filter((_, i) => i !== idx));

    const handleCloneTemplate = async (template) => {
        if (!template?.akz || !jobNr) return;
        setBusy(true); setError('');
        try {
            const res = await API.cloneSecureEntry(template.akz, jobNr);
            if (res?.success && res.msg?.akz) {
                cacheEntry(res.msg);
                appendAkz(res.msg.akz);
            } else {
                setError(res?.msg || 'Klonen fehlgeschlagen.');
            }
        } finally { setBusy(false); }
    };

    const openCreateDialog = (akz) => {
        setDraft({ akz: akz || '', name: '', description: '', comment: '', electric: false, mechanic: false, pls: false });
        setError('');
        setDialogOpen(true);
    };

    const handleDialogSubmit = async () => {
        if (!draft.akz?.trim()) { setError('AKZ ist erforderlich.'); return; }
        if (draft.akz.includes('#')) { setError("AKZ darf kein '#' enthalten."); return; }
        setBusy(true); setError('');
        try {
            const created = await API.createSecureEntry({
                akz: draft.akz.trim(),
                name: draft.name.trim(),
                description: draft.description.trim(),
                comment: draft.comment.trim(),
                electric: !!draft.electric,
                mechanic: !!draft.mechanic,
                pls: !!draft.pls
            });
            if (!created?.success) { setError(created?.msg || 'Eintrag konnte nicht angelegt werden.'); return; }
            if (created.msg) {
                cacheEntry(created.msg);
                setTemplates((prev) => [...prev, created.msg].sort((a, b) => (a.akz || '').localeCompare(b.akz || '')));
            }
            const cloned = await API.cloneSecureEntry(draft.akz.trim(), jobNr);
            if (!cloned?.success) { setError(cloned?.msg || 'Klonen fehlgeschlagen.'); return; }
            if (cloned.msg) cacheEntry(cloned.msg);
            appendAkz(cloned.msg.akz);
            setDialogOpen(false);
        } finally { setBusy(false); }
    };

    const openEditDialog = (akz) => {
        const entry = resolvedMap[akz];
        if (!entry) return;
        setEditAkz(akz);
        setEditDraft({
            name: entry.name || '',
            description: entry.description || '',
            comment: entry.comment || '',
            electric: !!entry.electric,
            mechanic: !!entry.mechanic,
            pls: !!entry.pls
        });
        setError('');
        setEditOpen(true);
    };

    const handleEditSubmit = async () => {
        if (!editAkz) return;
        setBusy(true); setError('');
        try {
            const res = await API.updateSecureEntry(editAkz, {
                name: editDraft.name.trim(),
                description: editDraft.description.trim(),
                comment: editDraft.comment.trim(),
                electric: !!editDraft.electric,
                mechanic: !!editDraft.mechanic,
                pls: !!editDraft.pls
            });
            if (!res?.success) { setError(res?.msg || 'Aktualisieren fehlgeschlagen.'); return; }
            if (res.msg) cacheEntry(res.msg);
            setEditOpen(false);
            setEditAkz(null);
        } finally { setBusy(false); }
    };

    const options = useMemo(() => templates, [templates]);

    if (!jobNr) {
        return (
            <Paper
                variant="outlined"
                sx={{
                    p: 2,
                    borderStyle: 'dashed',
                    borderColor: 'divider',
                    bgcolor: 'action.hover'
                }}
            >
                <Typography variant="body2" color="text.secondary">
                    Bitte zuerst den Auftrag speichern, um Sicherheitseinträge hinzuzufügen.
                </Typography>
            </Paper>
        );
    }

    return (
        <Box sx={{ width: '100%' }}>
            <Autocomplete
                fullWidth
                freeSolo
                selectOnFocus
                clearOnBlur
                handleHomeEndKeys
                size="medium"
                disabled={busy}
                options={options}
                value={null}
                inputValue={inputValue}
                onInputChange={(_, v) => setInputValue(v)}
                getOptionLabel={(opt) => {
                    if (typeof opt === 'string') return opt;
                    if (opt?.inputValue) return opt.inputValue;
                    if (opt?.akz) return `${opt.name || ''} – ${opt.akz}`.trim();
                    return '';
                }}
                isOptionEqualToValue={(opt, v) => opt?.akz === v?.akz}
                filterOptions={(opts, params) => {
                    const filtered = filter(opts, params);
                    const iv = params.inputValue;
                    const exists = opts.some((o) => (o.akz || '').toLowerCase() === iv.toLowerCase());
                    if (iv !== '' && !exists) {
                        filtered.unshift({
                            inputValue: iv,
                            newTemplate: true,
                            akz: iv,
                            name: `Neuen Eintrag anlegen: "${iv}"`
                        });
                    }
                    return filtered;
                }}
                renderOption={(props, option) => {
                    const { key, ...rest } = props;
                    if (option.newTemplate) {
                        return (
                            <li key={key || option.inputValue} {...rest}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <AddCircleOutlineIcon fontSize="small" sx={{ color: 'primary.light' }} />
                                    <em>{option.name}</em>
                                </Stack>
                            </li>
                        );
                    }
                    return (
                        <li key={key || option.akz} {...rest}>
                            <Stack spacing={0.25} sx={{ width: '100%' }}>
                                <Stack direction="row" spacing={1} alignItems="baseline" justifyContent="space-between">
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                        {option.name || '(ohne Name)'}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', color: 'primary.light' }}
                                    >
                                        {option.akz}
                                    </Typography>
                                </Stack>
                                {option.description && (
                                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                                        {String(option.description).slice(0, 80)}
                                    </Typography>
                                )}
                                <Stack direction="row" spacing={0.5} sx={{ mt: 0.25 }}>
                                    <PropIcon active={option.electric} icon={<BoltIcon />} label="Elekt." />
                                    <PropIcon active={option.mechanic} icon={<BuildIcon />} label="Mech." />
                                    <PropIcon active={option.pls} icon={<MemoryIcon />} label="PLS" />
                                </Stack>
                            </Stack>
                        </li>
                    );
                }}
                onChange={(_, value) => {
                    if (!value) return;
                    if (typeof value === 'string') { openCreateDialog(value); setInputValue(''); return; }
                    if (value.newTemplate) { openCreateDialog(value.inputValue); setInputValue(''); return; }
                    handleCloneTemplate(value); setInputValue('');
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        size="medium"
                        label="Sicherheitseintrag hinzufügen"
                        placeholder="AKZ oder Bezeichnung suchen"
                    />
                )}
                sx={{ mb: 2 }}
            />

            {error && (
                <Typography color="error" variant="caption" sx={{ display: 'block', mb: 1 }}>
                    {error}
                </Typography>
            )}

            <Paper
                variant="outlined"
                sx={{
                    p: entries.length === 0 ? 2 : 0,
                    borderStyle: entries.length === 0 ? 'dashed' : 'solid',
                    borderColor: 'divider',
                    bgcolor: entries.length === 0 ? 'action.hover' : 'background.paper',
                    overflow: 'hidden'
                }}
            >
                {entries.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                        Keine Sicherheitseinträge
                    </Typography>
                ) : (
                    entries.map((akz, index) => {
                        const entry = resolvedMap[akz] || resolvedMap[stripSuffix(akz)];
                        const displayAkz = stripSuffix(akz);
                        return (
                            <React.Fragment key={`${akz}-${index}`}>
                                {index > 0 && <Divider />}
                                <Box sx={{ p: 1.5, display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                                    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                                        <Stack direction="row" spacing={1.5} alignItems="baseline" sx={{ mb: 0.5 }}>
                                            <Typography
                                                variant="caption"
                                                sx={{
                                                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                                                    color: 'primary.dark',
                                                    px: 0.75,
                                                    py: 0.25,
                                                    borderRadius: 0.75,
                                                    bgcolor: (t) => t.palette.action.selected,
                                                    fontWeight: 600
                                                }}
                                            >
                                                {displayAkz}
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {entry?.name || '(ohne Name)'}
                                            </Typography>
                                            <Stack direction="row" spacing={0.5} sx={{ ml: 'auto !important' }}>
                                                <PropIcon active={entry?.electric} icon={<BoltIcon />} label="Elekt." />
                                                <PropIcon active={entry?.mechanic} icon={<BuildIcon />} label="Mech." />
                                                <PropIcon active={entry?.pls} icon={<MemoryIcon />} label="PLS" />
                                            </Stack>
                                        </Stack>
                                        {entry?.description && (
                                            <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                                                {entry.description}
                                            </Typography>
                                        )}
                                        {entry?.comment && (
                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={{ display: 'block', fontStyle: 'italic', mt: 0.5 }}
                                            >
                                                Bemerkung: {entry.comment}
                                            </Typography>
                                        )}
                                        {!entry && (
                                            <Typography variant="caption" color="text.secondary">
                                                Eintrag nicht gefunden (AKZ: {akz})
                                            </Typography>
                                        )}
                                    </Box>
                                    {entry && akz.includes('#') && (
                                        <Tooltip title="Bearbeiten">
                                            <IconButton size="small" onClick={() => openEditDialog(akz)}>
                                                <EditOutlinedIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                    <Tooltip title="Entfernen">
                                        <IconButton size="small" onClick={() => handleRemove(index)}>
                                            <DeleteOutlineIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </React.Fragment>
                        );
                    })
                )}
            </Paper>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth fullScreen={fullScreen}>
                <DialogTitle sx={{ pr: 6 }}>
                    Neuen Sicherheitseintrag anlegen
                    <IconButton onClick={() => setDialogOpen(false)} sx={{ position: 'absolute', right: 12, top: 12 }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2}>
                        <TextField
                            label="AKZ"
                            required
                            fullWidth
                            size="medium"
                            autoFocus
                            value={draft.akz}
                            onChange={(e) => setDraft({ ...draft, akz: e.target.value })}
                            helperText="Eindeutige Anlagenkennzeichnung (ohne '#')"
                        />
                        <TextField
                            label="Bezeichnung"
                            fullWidth
                            size="medium"
                            value={draft.name}
                            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                        />
                        <TextField
                            label="Beschreibung / Bemerkung"
                            fullWidth
                            size="medium"
                            multiline
                            minRows={2}
                            value={draft.description}
                            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                        />
                        <TextField
                            label="Kommentar"
                            fullWidth
                            size="medium"
                            value={draft.comment}
                            onChange={(e) => setDraft({ ...draft, comment: e.target.value })}
                        />
                        <Stack direction="row" spacing={2}>
                            <FormControlLabel
                                control={<Checkbox checked={draft.electric} onChange={(e) => setDraft({ ...draft, electric: e.target.checked })} />}
                                label={<Stack direction="row" spacing={0.5} alignItems="center"><BoltIcon fontSize="small" /> Elekt.</Stack>}
                            />
                            <FormControlLabel
                                control={<Checkbox checked={draft.mechanic} onChange={(e) => setDraft({ ...draft, mechanic: e.target.checked })} />}
                                label={<Stack direction="row" spacing={0.5} alignItems="center"><BuildIcon fontSize="small" /> Mech.</Stack>}
                            />
                            <FormControlLabel
                                control={<Checkbox checked={draft.pls} onChange={(e) => setDraft({ ...draft, pls: e.target.checked })} />}
                                label={<Stack direction="row" spacing={0.5} alignItems="center"><MemoryIcon fontSize="small" /> PLS</Stack>}
                            />
                        </Stack>
                        {error && <Typography color="error" variant="caption">{error}</Typography>}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => setDialogOpen(false)} disabled={busy}>Abbrechen</Button>
                    <Button onClick={handleDialogSubmit} variant="contained" disabled={busy} startIcon={<AddCircleOutlineIcon />}>
                        Anlegen & Hinzufügen
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth fullScreen={fullScreen}>
                <DialogTitle sx={{ pr: 6 }}>
                    Sicherheitseintrag bearbeiten
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}>
                        {stripSuffix(editAkz || '')}
                    </Typography>
                    <IconButton onClick={() => setEditOpen(false)} sx={{ position: 'absolute', right: 12, top: 12 }}>
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    <Stack spacing={2}>
                        <TextField
                            label="Bezeichnung"
                            fullWidth
                            size="medium"
                            value={editDraft.name}
                            onChange={(e) => setEditDraft({ ...editDraft, name: e.target.value })}
                        />
                        <TextField
                            label="Beschreibung / Bemerkung"
                            fullWidth
                            size="medium"
                            multiline
                            minRows={2}
                            value={editDraft.description}
                            onChange={(e) => setEditDraft({ ...editDraft, description: e.target.value })}
                        />
                        <TextField
                            label="Kommentar"
                            fullWidth
                            size="medium"
                            value={editDraft.comment}
                            onChange={(e) => setEditDraft({ ...editDraft, comment: e.target.value })}
                        />
                        <Stack direction="row" spacing={2}>
                            <FormControlLabel
                                control={<Checkbox checked={editDraft.electric} onChange={(e) => setEditDraft({ ...editDraft, electric: e.target.checked })} />}
                                label={<Stack direction="row" spacing={0.5} alignItems="center"><BoltIcon fontSize="small" /> Elekt.</Stack>}
                            />
                            <FormControlLabel
                                control={<Checkbox checked={editDraft.mechanic} onChange={(e) => setEditDraft({ ...editDraft, mechanic: e.target.checked })} />}
                                label={<Stack direction="row" spacing={0.5} alignItems="center"><BuildIcon fontSize="small" /> Mech.</Stack>}
                            />
                            <FormControlLabel
                                control={<Checkbox checked={editDraft.pls} onChange={(e) => setEditDraft({ ...editDraft, pls: e.target.checked })} />}
                                label={<Stack direction="row" spacing={0.5} alignItems="center"><MemoryIcon fontSize="small" /> PLS</Stack>}
                            />
                        </Stack>
                        {error && <Typography color="error" variant="caption">{error}</Typography>}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => setEditOpen(false)} disabled={busy}>Abbrechen</Button>
                    <Button onClick={handleEditSubmit} variant="contained" disabled={busy} startIcon={<SaveIcon />}>
                        Speichern
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
